import { Component } from '@theme/component';

/** Newsletter invitation, independent of cart modules and third-party event loading.
 * @extends {Component<{dialog: HTMLDialogElement, invitation?: HTMLElement, emailStep?: HTMLElement}>}
 */
class EvenmornPopup extends Component {
  requiredRefs = ['dialog'];
  #listeners;
  #timer;
  #consentTimer;
  #consentReady = false;
  #opener;
  #key = 'evenmorn-newsletter-until';
  #shown = false;
  #pending = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
    clearInterval(this.#consentTimer);
    this.#listeners?.abort();
    this.refs.dialog.close();
  }

  #initialize() {
    clearTimeout(this.#timer);
    clearInterval(this.#consentTimer);
    this.#consentReady = false;
    this.#pending = false;
    this.#listeners?.abort();
    if (!this.#isEligibleRoute()) return;
    if (this.querySelector('[data-newsletter-success]')) { this.#suppress(); return; }
    if (this.#suppressed()) return;
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    // Retry after a covering dialog closes, once the twenty-second deadline passes.
    document.addEventListener('close', () => {
      if (!this.#pending) return;
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#show(), 0);
    }, { capture: true, signal });
    document.addEventListener('visitorConsentCollected', this.#checkConsent, { signal });
    this.refs.dialog.addEventListener('cancel', () => this.#suppress(), { signal });
    this.refs.dialog.addEventListener('close', this.#closed, { signal });
    this.refs.dialog.addEventListener('click', (event) => {
      const dialog = this.refs.dialog;
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dismiss();
    }, { signal });
    // Shopify's banner is not a dialog and its API can arrive after this module.
    if (!this.#shown) {
      this.#consentTimer = setInterval(this.#checkConsent, 250);
      this.#checkConsent();
    }
  }

  #cookieBannerVisible() {
    const banner = document.getElementById('shopify-pc__banner');
    if (banner && banner.getClientRects().length && getComputedStyle(banner).visibility !== 'hidden') return true;
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.shouldShowBanner?.()) return false;
    const consent = privacy.currentVisitorConsent?.();
    return !['analytics', 'marketing', 'preferences'].some((key) => consent?.[key] === 'yes' || consent?.[key] === 'no');
  }

  #checkConsent = () => {
    if (this.#cookieBannerVisible()) {
      clearTimeout(this.#timer);
      this.#consentReady = false;
      this.#pending = false;
      return;
    }
    if (this.#consentReady) return;
    this.#consentReady = true;
    this.#timer = setTimeout(() => this.#show(), 20000);
  };

  #isEligibleRoute() {
    return location.pathname === new URL(this.dataset.homeUrl, location.origin).pathname;
  }

  #suppressed() {
    try { return Number(localStorage.getItem(this.#key)) > Date.now(); } catch { return false; }
  }

  #show() {
    if (this.#shown || !this.isConnected || !this.#isEligibleRoute() || this.#suppressed()) return;
    if (this.#cookieBannerVisible()) { this.#checkConsent(); return; }
    clearTimeout(this.#timer);
    this.#pending = Boolean(document.querySelector('dialog[open]'));
    if (this.#pending) return;
    this.#opener = document.activeElement;
    this.refs.dialog.showModal();
    this.#shown = true;
    clearInterval(this.#consentTimer);
  }

  #suppress() {
    try { localStorage.setItem(this.#key, String(Date.now() + 7 * 24 * 60 * 60 * 1000)); } catch { /* Storage can be blocked. */ }
  }

  #closed = () => {
    this.#suppress();
    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) this.#opener.focus();
  };

  /** Dismiss via close button, backdrop, or the native Escape key. */
  dismiss() {
    this.#suppress();
    this.refs.dialog.close();
  }

  /** Advance to the existing Shopify newsletter form. */
  next() {
    if (this.refs.invitation) this.refs.invitation.hidden = true;
    if (this.refs.emailStep) this.refs.emailStep.hidden = false;
    this.refs.emailStep?.querySelector('input[type="email"]')?.focus();
  }
}

if (!customElements.get('evenmorn-popup-component')) customElements.define('evenmorn-popup-component', EvenmornPopup);
